import { useState, useEffect } from 'react';
import { useNostr } from '@nostrify/react';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';

interface UseStreamingQueryResult {
  events: NostrEvent[];
  /** True until the first EOSE (or timeout) is received — use for skeletons */
  isLoading: boolean;
  /** True while still receiving events (even after EOSE, until timeout) */
  isStreaming: boolean;
}

/** Returns true for addressable event kinds (30000–39999). */
function isAddressable(kind: number): boolean {
  return kind >= 30000 && kind < 40000;
}

/** Returns true for replaceable event kinds (10000–19999, plus 0 and 3). */
function isReplaceable(kind: number): boolean {
  return kind === 0 || kind === 3 || (kind >= 10000 && kind < 20000);
}

/**
 * Stable dedup key for an event:
 * - Addressable (30000–39999): "kind:pubkey:d"
 * - Replaceable (10000–19999, 0, 3): "kind:pubkey"
 * - Regular: event id
 */
function dedupKey(event: NostrEvent): string {
  if (isAddressable(event.kind)) {
    const d = event.tags.find(([n]) => n === 'd')?.[1] ?? '';
    return `${event.kind}:${event.pubkey}:${d}`;
  }
  if (isReplaceable(event.kind)) {
    return `${event.kind}:${event.pubkey}`;
  }
  return event.id;
}

/**
 * Streams Nostr events from relays as they arrive, updating state piece-by-piece.
 * Unlike `nostr.query()` which waits for EOSE, this pushes each EVENT into state
 * immediately, giving a progressive-reveal experience.
 *
 * - Shows skeletons only until the first EOSE from any relay.
 * - Continues accepting events until `timeoutMs` elapses.
 * - Deduplicates regular events by id; addressable/replaceable events by their
 *   logical coordinate so duplicates from multiple relays are collapsed.
 * - Re-runs when the `queryKey` changes (dependency array).
 *
 * @param filters     Nostr filter array (memoized by caller)
 * @param queryKey    Stable identity array — change it to re-fetch (like useQuery's queryKey)
 * @param timeoutMs   Hard stop for the subscription (default 15 000 ms)
 */
export function useStreamingQuery(
  filters: NostrFilter[],
  queryKey: unknown[],
  timeoutMs = 15000,
): UseStreamingQueryResult {
  const { nostr } = useNostr();
  const [events, setEvents] = useState<NostrEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isStreaming, setIsStreaming] = useState(true);

  const newKey = JSON.stringify(queryKey);

  useEffect(() => {
    setEvents([]);
    setIsLoading(true);
    setIsStreaming(true);

    const controller = new AbortController();
    const { signal } = controller;
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    // Map from dedup key → current best event (newest created_at wins)
    const best = new Map<string, NostrEvent>();

    (async () => {
      try {
        for await (const msg of nostr.req(filters, { signal })) {
          if (msg[0] === 'EVENT') {
            const event = msg[2] as NostrEvent;
            const key = dedupKey(event);
            const existing = best.get(key);

            if (!existing) {
              // New entry — add to state and immediately drop the skeleton
              best.set(key, event);
              setIsLoading(false);
              setEvents(prev => [...prev, event]);
            } else if (event.created_at > existing.created_at) {
              // Newer version of a replaceable/addressable event — replace in state
              best.set(key, event);
              setEvents(prev => prev.map(e => (e.id === existing.id ? event : e)));
            }
            // Older duplicate — ignore
          } else if (msg[0] === 'EOSE') {
            // All stored events from this relay delivered — drop skeleton if nothing arrived yet
            setIsLoading(false);
          } else if (msg[0] === 'CLOSED') {
            break;
          }
        }
      } catch {
        // AbortError on timeout is expected
      } finally {
        setIsLoading(false);
        setIsStreaming(false);
        clearTimeout(timeout);
      }
    })();

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newKey, timeoutMs]);

  return { events, isLoading, isStreaming };
}
